import React, { useState } from "react";
import { Form, Button, Col } from "react-bootstrap";
import DatePicker from 'react-datepicker';
import "../Frontend/UserProfile/UserProfile.css";
import { useBanner } from "../../context/BannerContext";
import useAuth from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";

const AddDemoRequestFormV2 = () => {
  useAuth(); // Ensure the user is authenticated

  const showBanner = useBanner();
  const navigate = useNavigate();
  const [loading, setLoader] = useState(false);
  const [customError, setCustomError] = useState({});
  const [productListWrongDataError, setProductListWrongDataError] = useState(false);

  const requiredHeaders = ["Product", "Description"];

  const [formData, setFormData] = useState({
    demoDateTime: "",
    question: "",
    sellerAccountWebsite: "",
    buyerAccountWebsite: "",
    lead_id: "",
    clientLinkedinUrl: "",
    productListCsv: null,
    csvData: "",
    exploringTools: "",
    kpi: "",
    growthFocus: "",
    salesTeamSize: "",
    decisionMakers: [],
  });

  const isToday = (date) => {
    if (!date || !(date instanceof Date)) return false;
    return date.toDateString() === new Date().toDateString();
  };

  const validateLinkedinURL = (url) => {
    const linkedInRegex =
      /^(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/[a-zA-Z0-9-_]+\/?$/;
    return linkedInRegex.test(url.trim());
  };

  const handleChange = (e) => {
    const { name, value, files, type } = e.target;
    
    if (type === "checkbox") {
      // handle multiple selections for checkbox groups
      const [group, option] = name.split(".");
      setFormData((prev) => {
        const current = prev[group] || [];
        if (current.includes(option)) {
          return { ...prev, [group]: current.filter((v) => v !== option) };
        } else {
          return { ...prev, [group]: [...current, option] };
        }
      });
    }else if (name === "productListCsv") {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const lines = text.trim().split("\n");

        if (lines.length < 2) {
          setCustomError((prevErrors) => ({
            ...prevErrors,
            productListCsv: "CSV file is empty or missing data.",
          }));
          return;
        }

        const headers = lines[0].split(",").map((h) => h.trim());
        const missingHeaders = requiredHeaders.filter(
          (h) => !headers.includes(h)
        );

        if (missingHeaders.length > 0) {
          setCustomError((prevErrors) => ({
            ...prevErrors,
            productListCsvFormatError: `Missing required column(s): ${missingHeaders.join(
              ", "
            )}`,
          }));
          setProductListWrongDataError(true);
          return;
        } else {
          setProductListWrongDataError(false);
        }

        // Convert to JSON (enhanced CSV parsing)
        const json = lines.slice(1).map((line) => {
          const values = [];
          let current = "";
          let insideQuotes = false;

          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = line[i + 1];

            if (char === '"' && !insideQuotes) {
              insideQuotes = true;
            } else if (char === '"' && insideQuotes) {
              if (nextChar === '"') {
                current += '"'; // Escaped quote
                i++; // Skip next quote
              } else {
                insideQuotes = false;
              }
            } else if (char === "," && !insideQuotes) {
              values.push(current.trim());
              current = "";
            } else {
              current += char;
            }
          }

          values.push(current.trim()); // push last value

          // Clean up quotes and escapes
          const cleanedValues = values.map((v) =>
            v.replace(/^"(.*)"$/, "$1").replace(/\\"/g, '"')
          );

          const obj = {};
          headers.forEach((header, i) => {
            obj[header] = cleanedValues[i] || "";
          });
          return obj;
        })
        .filter((row) => row[headers[0]].trim() !== "") // Filter out rows with empty product name
        .map((row) => ({
          name: row["Product"] || row["product"] || "",
          description: row["Description"] || row["description"] || "",
        }));

        setFormData((prev) => ({
          ...prev,
          csvData: JSON.stringify(json),
          productListCsv: files[0], // store the file too if needed
        }));
        setCustomError((prevErrors) => ({
          ...prevErrors,
          productListCsv: "",
        }));
      };

      if (files?.[0] instanceof Blob) {
        reader.readAsText(files?.[0]);
      } else {
        setFormData({ ...formData, [name]: null });
        setCustomError((prevErrors) => ({
          ...prevErrors,
          productListCsv: "CSV file is empty or missing data.",
        }));
        return;
      }

      setCustomError((prevErrors) => ({
        ...prevErrors,
        productListCsv: "",
        productListCsvFormatError: "",
      }));
    } else {
      setFormData({ ...formData, [name]: value });
      // Clear the error for the current field
      setCustomError((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  const handleClose = () => {
    navigate('/sales_accounts');
  }

  const sumbitForm = async (formDataParams) => {
    const formData = new FormData();
    formData.append("sellerAccountWebsite", formDataParams.sellerAccountWebsite);
    formData.append("buyerAccountWebsite", formDataParams.buyerAccountWebsite);
    formData.append("demoDateTime", formDataParams.demoDateTime);
    formData.append("question", formDataParams.question);
    formData.append("lead_id", formDataParams.lead_id);
    formData.append("clientLinkedinUrl", formDataParams.clientLinkedinUrl);
    formData.append("csvData", formDataParams.csvData);

    formData.append("exploringTools", formDataParams.exploringTools);
    formData.append('kpi', formDataParams.kpi);
    formData.append("growthFocus", formDataParams.growthFocus);
    formData.append("salesTeamSize", formDataParams.salesTeamSize);
    formData.append("decisionMakers", formDataParams.decisionMakers);

    if (formDataParams.productListCsv) {
      formData.append("productListCsv", formDataParams.productListCsv); // Append file
    }

    setLoader(true);
    
    const response = await api.post("/demo_request/add_demo_request", formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });

    setLoader(false);
    let result = response?.data ?? [];

    return result;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newError = {};
    let isValid = true;

    const websiteUrlRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[^\s]*)?$/i;

    if (formData.demoDateTime == "") {
      newError.demoDateTime = "Demo date-time required.";
      isValid = false;
    }

    // Website validation
    if (!formData.sellerAccountWebsite.trim()) {
      newError.sellerAccountWebsite = "Seller Account Website is required.";
      isValid = false;
    } else if (!websiteUrlRegex.test(formData.sellerAccountWebsite)) {
      newError.sellerAccountWebsite =
        "Enter valid Website url.";
      isValid = false;
    }

    if (!formData.buyerAccountWebsite.trim()) {
      newError.buyerAccountWebsite = "Buyer Account Website is required.";
      isValid = false;
    } else if (!websiteUrlRegex.test(formData.buyerAccountWebsite)) {
      newError.buyerAccountWebsite =
        "Enter valid Website url";
      isValid = false;
    }
    // End: Website validation

    if (!formData.clientLinkedinUrl.trim()) {
      newError.clientLinkedinUrl = "Client linkedin is required.";
      isValid = false;
    } else if (!validateLinkedinURL(formData.clientLinkedinUrl)) {
      newError.clientLinkedinUrl = "Invalid client linkedin url.";
      isValid = false;
    }

    if (!formData?.lead_id.trim()) {
      newError.lead_id = "Lead ID is required.";
      isValid = false;
    }

    if (formData.productListCsv == null) {
      newError.productListCsv = "Product list csv required.";
      isValid = false;
    } else if (productListWrongDataError) {
      newError.productListCsvFormatError =
        "Missing required column(s): Product, Description";
      isValid = false;
    }

    setCustomError(newError);
    if (!isValid) return;

    const response = await sumbitForm(formData);

    console.log("response :: ", response)

    if(response.status == 200 || response.status == 201){
      showBanner({
        message: "Demo request submitted successfully!",
        title: "Alert",
        type: "success",
        duration: 10000,
      });

      navigate('/sales_accounts');
    }else{
      showBanner({
          message: "Something went wront. Please try later.",
          title: "Alert",
          type: "error",
          duration: 10000,
      });
    }
    
  };

  return (
    <div className="user_profile_wrapper request_demo_form_wrapper">
        <div className="container-fluid">
            <div className="user_profile_subblock mt-1 p-4">

                <div className="row">
                  <div className="col-md-12">
                    <h4 className="blue add_user_heading">Add Demo Request</h4>
                  </div>
                </div>

                <Form onSubmit={handleSubmit} className="" autoComplete="off">
                <div className="row">
                        <Col md={4} className="mb-3">
                        <Form.Group controlId="lead_id">
                            <Form.Label>CRM Lead ID</Form.Label>
                            <Form.Control
                            type="text"
                            className="popupInputStyle"
                            name="lead_id"
                            value={formData.lead_id}
                            onChange={handleChange}
                            autoComplete="off"
                            />
                            {customError.lead_id && (
                            <p className="text-danger">
                                {customError.lead_id}
                            </p>
                            )}
                        </Form.Group>
                        </Col>
    
                        <Col md={4} className="mb-3">
                        <Form.Group controlId="demoDateTime">
                            <Form.Label>
                            Demo Date & Time{" "}
                            <span style={{ fontSize: "0.9em", color: "gray" }}>
                                (All times are in IST)
                            </span>
                            </Form.Label>
                            <DatePicker
                            name="demoDateTime"
                            selected={formData.demoDateTime}
                            showTimeSelect
                            onChange={(date) => {
                                setFormData({ ...formData, demoDateTime: date });
                                setCustomError((prevErrors) => ({
                                ...prevErrors,
                                demoDateTime: "",
                                }));
                            }}
                            timeIntervals={60}
                            timeCaption="Time"
                            dateFormat="MMMM d, yyyy h:mm aa"
                            timeFormat="h aa"
                            className="form-control popupInputStyle"
                            minDate={new Date()}
                            minTime={
                                isToday(formData.demoDateTime)
                                ? new Date()
                                : new Date(new Date().setHours(0, 0, 0, 0))
                            }
                            maxTime={new Date(new Date().setHours(23, 59))}
                            autoComplete="off"
                            onKeyDown={(e) => e.preventDefault()}
                            />
                            {customError.demoDateTime && (
                            <p className="text-danger">
                                {customError.demoDateTime}
                            </p>
                            )}
                        </Form.Group>
                        </Col>
    
                        <Col md={4} className="mb-3">
                        <Form.Group controlId="clientLinkedinUrl">
                            <Form.Label>Client Linkedin Url</Form.Label>
                            <Form.Control
                            type="text"
                            className="popupInputStyle"
                            name="clientLinkedinUrl"
                            value={formData.clientLinkedinUrl}
                            onChange={handleChange}
                            autoComplete="off"
                            />
                            {customError.clientLinkedinUrl && (
                            <p className="text-danger">
                                {customError.clientLinkedinUrl}
                            </p>
                            )}
                        </Form.Group>
                        </Col>

                        <Col md={4} className="mb-3">
                        <Form.Group controlId="sellerAccountWebsite">
                            <Form.Label>Seller Account Website</Form.Label>
                            <Form.Control
                            type="text"
                            className="popupInputStyle"
                            name="sellerAccountWebsite"
                            value={formData.sellerAccountWebsite}
                            onChange={handleChange}
                            autoComplete="off"
                            />
                            {customError.sellerAccountWebsite && (
                            <p className="text-danger">
                                {customError.sellerAccountWebsite}
                            </p>
                            )}
                        </Form.Group>
                        </Col>
    
                        <Col md={4} className="mb-3">
                        <Form.Group controlId="buyerAccountWebsite">
                            <Form.Label>Buyer Account Website</Form.Label>
                            <Form.Control
                            type="text"
                            className="popupInputStyle"
                            name="buyerAccountWebsite"
                            value={formData.buyerAccountWebsite}
                            onChange={handleChange}
                            autoComplete="off"
                            />
                            {customError.buyerAccountWebsite && (
                            <p className="text-danger">
                                {customError.buyerAccountWebsite}
                            </p>
                            )}
                        </Form.Group>
                        </Col>

                        <Col md={4}></Col>

                        <Col md={4} className="mb-3">
                        <Form.Group controlId="productListCsv">
                            <Form.Label>
                            Product List (CSV)
                            <a
                                href="../sample_files/Sample Product List.csv"
                                download
                                className="ml-2 mt-2 text-primary"
                            >
                                Sample
                            </a>
                            </Form.Label>
                            <Form.Control
                            type="file"
                            className="popupInputStyle"
                            name="productListCsv"
                            accept=".csv"
                            onChange={handleChange}
                            />
                            {customError.productListCsv && (
                            <p className="text-danger">
                                {customError.productListCsv}
                            </p>
                            )}
                            {customError.productListCsvFormatError && (
                            <p className="text-danger">
                                {customError.productListCsvFormatError}
                            </p>
                            )}
                        </Form.Group>
                        </Col>
    
                        <Col md={9} className="mb-3">
                        <Form.Group controlId="question">
                            <Form.Label>What tools are your reps using today to research accounts or find new opportunities?</Form.Label>
                            <textarea
                            type="textarea"
                            className="popupInputStyle form-control"
                            rows={1}
                            name="question"
                            value={formData.question}
                            onChange={handleChange}
                            autoComplete="off"
                            />
                            {customError.question && (
                            <p className="text-danger">{customError.question}</p>
                            )}
                        </Form.Group>
                    </Col>
                
                    {/* Exploring Tools */}
                    <Col md={6} className="mb-3">
                    <Form.Group >
                        <Form.Label>
                        Are you currently exploring new GTM or sales tools?
                        </Form.Label>
                        <div>
                        {["Yes", "No", "Not Sure"].map((val) => (
                            <Form.Check
                            inline
                            type="radio"
                            label={val}
                            name="exploringTools"
                            className="radio_custom_btn"
                            value={val}
                            checked={formData.exploringTools === val}
                            onChange={handleChange}
                            key={val}
                            />
                        ))}
                        </div>
                    </Form.Group>
                    </Col>

                    <Col md={9} className="mb-3">
                        <Form.Group controlId="kpi">
                            <Form.Label>What KPIs are falling short today — account research, building pipeline, conversion ratio, quota attainment, ramp time, or something else?</Form.Label>
                            <textarea
                            type="textarea"
                            className="popupInputStyle form-control"
                            rows={1}
                            name="kpi"
                            value={formData.kpi}
                            onChange={handleChange}
                            autoComplete="off"
                            />
                            {customError.kpi && (
                            <p className="text-danger">{customError.kpi}</p>
                            )}
                        </Form.Group>
                    </Col>

                    {/* Growth Focus */}
                    <Col md={12} className="mb-3">
                        <Form.Group >
                            <Form.Label>What is your focus this year ?</Form.Label>
                            <div>
                            {["Expanding in existing accounts", "Winning new logos", "Both"].map(
                                (val) => (
                                <Form.Check
                                    key={val}
                                    inline
                                    type="radio"
                                    className="radio_custom_btn"
                                    name="growthFocus"
                                    label={val}
                                    value={val}
                                    checked={formData.growthFocus === val}
                                    onChange={handleChange}
                                />
                                )
                            )}
                            </div>
                        </Form.Group>
                    </Col>

                    {/* Sales Team Size */}
                    <Col md={12} className="mb-3">
                        <Form.Group>
                            <Form.Label>What is the size of the current sales team ?</Form.Label>
                            <div>
                            {["<10", "10–50", "51–200", "200+"].map((option) => (
                                <Form.Check
                                key={option}
                                inline
                                type="radio"
                                className="radio_custom_btn"
                                name="salesTeamSize"
                                label={option}
                                value={option}
                                checked={formData.salesTeamSize === option}
                                onChange={handleChange}
                                />
                            ))}
                            </div>
                        </Form.Group>
                    </Col>

                    {/* Decision Makers */}
                    <Col md={12} className="mb-3">
                        <Form.Group >
                            <Form.Label>
                            Who usually gets involved in choosing tools like this?
                            </Form.Label>
                            <div className="">
                            {[
                                "Sales",
                                "Marketing",
                                "RevOps",
                                "Customer Success",
                            ].map((option) => (
                                <Form.Check
                                    key={option}
                                    type="checkbox"
                                    className="form-check-inline radio_custom_btn"
                                    label={option}
                                    name={`decisionMakers.${option}`}
                                    checked={formData.decisionMakers.includes(option)}
                                    onChange={handleChange}
                                />
                            ))}
                            </div>
                        </Form.Group>
                    </Col>

                    {/* Submit */}
                    <div className="d-flex justify-content-end mt-4 gap-2">
                        <Button
                        variant="secondary"
                        onClick={handleClose}
                        className="cancel_button_demo_request"
                        >
                        Cancel
                        </Button>
                        <Button
                        type="submit"
                        disabled={loading}
                        variant="primary"
                        className="submit_button_demo_request"
                        >
                        {loading ? (
                          <i className="fa fa-spinner fa-spin"></i>
                        ) : (
                          <></>
                        )}{" "}
                        
                        Submit
                        </Button>
                    </div>
                </div>
                </Form>
            </div>
        </div>
    </div>
  );
};

export default AddDemoRequestFormV2;